import React from "react";
import { IOption } from "../../../hooks/UseDropdown";
import { CRMIcon } from "../../CRMIcon/CRMIcon";
import UnstyledInputGeneralComponent from "../../UnstyledInputGeneralComponent/UnstyledInputGeneralComponent";
import { SpacingRow } from "../../BuildingBlocks/SpacingRow";
import { CRMSpacing } from "../../../models/CRMSpacing";
import { CRMFontSizes } from "../../../models/CRMFontSizes";
import { WeightBold } from "../../WeightBold/WeightBold";
import { Padding } from "../../BuildingBlocks/Padding";
import { CRMEmptyPlaceholder } from "../CRMEmptyPlaceholder/CRMEmptyPlaceholder";
import { TFormStatus } from "../../../../../shared/src/codecs/codec";
import { CRMDropdownOptionBox } from "../CRMDropdownOptionBox/CRMDropdownOptionBox";
import { useFilterDropdown } from "../../../hooks/useFilterDropdown";
import { FuzzySearchHighlightedText } from "../../../hooks/useFuzzysort";
import { CRMLoadingBar } from "../CRMLoadingBar/CRMLoadingBar";
import { CRMDropdownFace } from "../CRMDropdownFace/CRMDropdownFace";
import { TextColor } from "../../BuildingBlocks/TextColor";
import { CRMColors } from "../../../models/CRMColors";
import { CRMDropdownOption } from "../CRMDropdownOption/CRMDropdownOption";

interface ICRMFilterDropdownProps<A extends string> {
    value: A;
    valuePrefix?: string;
    placeholder?: string;
    options: Array<IOption<A>>;
    onChange: (value: A) => void;
    
    searchStatus?: TFormStatus;
    searchText?: string;
    preserveSearch?: boolean;
    onSetSearchText?: (value: string) => void;
    
    displayError?: boolean;
    disabled?: boolean;
}

export const CRMFilterDropdown = <A extends string>(props: ICRMFilterDropdownProps<A>): JSX.Element => {

    const {
        dropdownRef,
        filterInputRef,
        isOpen,
        toggleDropdown,
        getFaceText,
        getFaceStatus,
        getOptionBoxStatus,
        isOptionSelected,
        onSelectOption,
        onChangeFilterText,
        getOptions,
        isLoadingResults
    } = useFilterDropdown({
        value: props.value,
        options: props.options,
        filterText: props.searchText,
        filterStatus: props.searchStatus,
        onChange: props.onChange,
        preserveFilterText: props.preserveSearch,
        setFilterText: props.onSetSearchText,
    });

    return (
        <div
            ref={dropdownRef}
            className="crm-filter-dropdown"
        >
            {/* FACE */}
            <CRMDropdownFace
                isOpen={isOpen}
                toggleOpen={toggleDropdown}
                displayError={props.displayError}
                disabled={props.disabled}
            >
                {getFaceStatus() === "open" &&
                    <SpacingRow spacing={CRMSpacing.TINY}>
                        <CRMIcon
                            iconName="search-list"
                            size="small"
                            colour="neutral-ink"
                        />
                        <UnstyledInputGeneralComponent
                            inputRef={filterInputRef}
                            className="crm-filter-dropdown__face-input"
                            inputType="text"
                            value={props.searchText}
                            displayError={false}
                            onChange={onChangeFilterText}
                            onPressEnterKey={() => null}
                        />
                    </SpacingRow>
                }

                {getFaceStatus() === "closed-selected" &&
                    <SpacingRow spacing={CRMSpacing.TINY}>
                        {!!props.valuePrefix &&
                            <WeightBold>{props.valuePrefix}</WeightBold> 
                        }
                        <span>{getFaceText()}</span>
                    </SpacingRow>
                }

                {getFaceStatus() === "closed-empty" &&
                    <TextColor color={CRMColors.NEUTRAL_4}>
                        {props.placeholder || "Select Value"}
                    </TextColor>
                }
            </CRMDropdownFace>

            {/* OPTIONS */}
            {isOpen &&
                <CRMDropdownOptionBox>
                    {isLoadingResults() && 
                        <CRMLoadingBar duration={1000} />
                    }
                    
                    {getOptionBoxStatus() === "no-search-made" &&
                        <Padding spacing={CRMSpacing.MEDIUM}>
                            <CRMEmptyPlaceholder
                                iconName="search-list"
                                iconSize="50px" 
                                textGap={"0px"}
                                fontSize={CRMFontSizes.SMALL}
                            >
                                No search made yet
                            </CRMEmptyPlaceholder>
                        </Padding>
                    }

                    {getOptionBoxStatus() === "searching-on-empty" &&
                        <Padding spacing={CRMSpacing.MEDIUM}>
                            <CRMEmptyPlaceholder
                                iconName="search-list"
                                iconSize="50px" 
                                textGap={"0px"}
                                fontSize={CRMFontSizes.SMALL}
                            >
                                Searching...
                            </CRMEmptyPlaceholder>
                        </Padding>
                    }
                    
                    {getOptionBoxStatus() === "no-match" &&
                        <Padding spacing={CRMSpacing.MEDIUM}>
                            <CRMEmptyPlaceholder
                                iconSize="50px" 
                                textGap={"0px"}
                                fontSize={CRMFontSizes.SMALL}
                            >
                                No match
                            </CRMEmptyPlaceholder>
                        </Padding>
                    }

                    {getOptionBoxStatus() === "match" && 
                        getOptions().map((result, key) => (
                            <CRMDropdownOption
                                key={key}
                                isSelected={isOptionSelected(result.obj.value as A)}
                                label={
                                    <FuzzySearchHighlightedText
                                        result={result}
                                        className="crm-filter-dropdown__option-highlight"
                                    />
                                }
                                onClick={() => onSelectOption({
                                    label: result.obj.label,
                                    value: result.obj.value as A
                                })}
                            />
                        ))
                    }
                </CRMDropdownOptionBox>
            }
        </div>
    );
};
